import { useBankAccountFieldsConfig } from '@commenda/api';
import {
  FieldWrapper,
  Icons,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  SingleCombobox,
  Tooltip,
} from '@commenda/kakashi';
import { FieldsConfig, IBankAccountCredentials } from '@commenda/types';
import {
  ALL_CURRENCIES,
  getAllCountries,
  getCountryData,
  getDefaultCurrency,
  getFieldDef,
  getFieldDescription,
  getFieldLabelForCountry,
  getOptionalFields,
  getRequiredFields,
  getValidationRule,
} from '../form-builder/config/country-bank-utils';
import { formatAccountType } from './utils';

// ─── Props ─────────────────────────────────────────────────────────────────────

interface BankAccountFieldsProps {
  /** Layer 1 base fields */
  accountHolderName: string;
  country: string;
  bankName: string;
  currency: string;

  /** Layer 2 country-specific credential values keyed by JSON field key (snake_case) */
  credentials: IBankAccountCredentials;

  /** Flat error map keyed by field key or base field name */
  errors: Record<string, string>;

  onBaseFieldChange: (
    field: 'accountHolderName' | 'country' | 'bankName' | 'currency',
    value: string
  ) => void;
  onCredentialChange: (fieldKey: string, value: string) => void;
  /** Called after blur validation; replaces the full errors map */
  onErrorChange: (errors: Record<string, string>) => void;

  /** 'any' — user can select any country. 'locked' — locked to entity's country. 'locked-default' — locked to configured default country. */
  bankCountryScope?: { mode: 'any' | 'locked' | 'locked-default' };
  disabled?: boolean;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function InfoIcon({ tooltip }: { tooltip: string }) {
  return (
    <Tooltip content={tooltip} side="top">
      <Icons.InfoCircle className="size-4 text-text-tertiary cursor-default" />
    </Tooltip>
  );
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="txt-medium-bold text-text-primary">{title}</p>
      <p className="txt-small-regular text-text-secondary">{subtitle}</p>
    </div>
  );
}

function FieldLabel({
  htmlFor,
  children,
  tooltip,
}: {
  htmlFor: string;
  children: React.ReactNode;
  tooltip?: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <Label htmlFor={htmlFor} className="txt-small-regular text-text-tertiary">
        {children}
      </Label>
      {tooltip && <InfoIcon tooltip={tooltip} />}
    </div>
  );
}

// Inner component — only rendered after config is loaded, so config is always defined.
function BankAccountFieldsInner({
  config,
  accountHolderName,
  country,
  bankName,
  currency,
  credentials,
  errors,
  onBaseFieldChange,
  onCredentialChange,
  onErrorChange,
  bankCountryScope,
  disabled,
}: BankAccountFieldsProps & { config: FieldsConfig }) {
  const allCountries = getAllCountries(config);
  const countryData = country ? getCountryData(config, country) : null;
  const requiredFields = country ? getRequiredFields(config, country) : [];
  const optionalFields = country ? getOptionalFields(config, country) : [];
  const isLocked =
    bankCountryScope?.mode === 'locked' ||
    bankCountryScope?.mode === 'locked-default';

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleBaseChange = (
    field: 'accountHolderName' | 'country' | 'bankName' | 'currency',
    value: string
  ) => {
    onBaseFieldChange(field, value);
    if (errors[field]) onErrorChange({ ...errors, [field]: '' });
  };

  const handleCountryChange = (value: string) => {
    onBaseFieldChange('country', value);
    const newCurrency = getDefaultCurrency(value);
    if (newCurrency) onBaseFieldChange('currency', newCurrency);
    onErrorChange({});
  };

  const handleBaseBlur = (
    field: 'accountHolderName' | 'bankName',
    value: string
  ) => {
    const next = { ...errors };
    if (field === 'accountHolderName') {
      if (!value.trim())
        next.accountHolderName = 'Enter the account holder name.';
      else if (value.length > 140)
        next.accountHolderName =
          'Account holder name must be 140 characters or fewer.';
      else next.accountHolderName = '';
    } else if (!value.trim()) next.bankName = 'Enter the bank name.';
    else if (value.length > 120)
      next.bankName = 'Bank name must be 120 characters or fewer.';
    else next.bankName = '';
    onErrorChange(next);
  };

  const handleCredentialChange = (fieldKey: string, value: string) => {
    onCredentialChange(fieldKey, value);
    if (errors[fieldKey]) onErrorChange({ ...errors, [fieldKey]: '' });
  };

  const handleCredentialBlur = (fieldKey: string, value: string) => {
    const stripped = value.replace(/-/g, '');
    if (stripped !== value) onCredentialChange(fieldKey, stripped);

    const rule = getValidationRule(config, country, fieldKey);
    const label = getFieldLabelForCountry(config, country, fieldKey);
    const countryName = countryData?.name ?? country;
    let error = '';

    if (!stripped.trim()) {
      if (requiredFields.includes(fieldKey))
        error = `Enter the ${label} for your ${countryName} account.`;
    } else if (rule?.pattern && !new RegExp(rule.pattern).test(stripped))
      error = `${label} doesn't match the expected format.`;
    else if (rule?.length && stripped.length !== rule.length)
      error = `${label} must be exactly ${rule.length} characters.`;
    else if (rule?.min_length && stripped.length < rule.min_length)
      error = `${label} must be at least ${rule.min_length} characters.`;

    onErrorChange({ ...errors, [fieldKey]: error });
  };

  // ── Layer 2 field renderer ────────────────────────────────────────────────

  const renderLayer2Field = (fieldKey: string) => {
    const label = getFieldLabelForCountry(config, country, fieldKey);
    const fieldDef = getFieldDef(config, fieldKey);
    const isEnumField = fieldDef?.type === 'enum';

    if (isEnumField && fieldDef?.options) {
      const options = fieldDef.options.map((o) => ({
        value: o,
        label: formatAccountType(o),
      }));
      return (
        <FieldWrapper key={fieldKey}>
          <FieldLabel htmlFor={fieldKey}>{label}</FieldLabel>
          <SingleCombobox
            options={options}
            value={credentials[fieldKey] ?? ''}
            setValue={(val) => handleCredentialChange(fieldKey, val)}
            placeholder={`Select ${label.toLowerCase()}`}
            error={errors[fieldKey]}
            canClearSelection={false}
            disabled={disabled}
            renderTrigger={(val) => (
              <span className="txt-sm-regular text-text-secondary">
                {val.selectedOption?.label || val.placeholder}
              </span>
            )}
          />
        </FieldWrapper>
      );
    }

    return (
      <FieldWrapper key={fieldKey}>
        <FieldLabel htmlFor={fieldKey}>{label}</FieldLabel>
        <Input
          id={fieldKey}
          data-testid={`bank-${fieldKey}`}
          type="text"
          value={credentials[fieldKey] ?? ''}
          onChange={(e) => handleCredentialChange(fieldKey, e.target.value)}
          onBlur={(e) => handleCredentialBlur(fieldKey, e.target.value)}
          placeholder={`Enter ${label}`}
          error={errors[fieldKey]}
          className="h-11"
          disabled={disabled}
        />
      </FieldWrapper>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">
      {/* Section 1 — Account information */}
      <div className="flex flex-col gap-5">
        <SectionHeader
          title="Account information"
          subtitle="General details that apply across all countries."
        />
        <div className="flex flex-col gap-5">
          <FieldWrapper>
            <FieldLabel
              htmlFor="accountHolderName"
              tooltip={getFieldDescription(config, 'account_holder_name')}
            >
              Account holder name
            </FieldLabel>
            <Input
              id="accountHolderName"
              data-testid="bank-accountHolderName"
              type="text"
              value={accountHolderName}
              onChange={(e) =>
                handleBaseChange('accountHolderName', e.target.value)
              }
              onBlur={(e) =>
                handleBaseBlur('accountHolderName', e.target.value)
              }
              placeholder="Enter account holder name"
              maxLength={140}
              error={errors.accountHolderName}
              className="h-11"
              disabled={disabled}
            />
          </FieldWrapper>

          <FieldWrapper>
            <FieldLabel
              htmlFor="country"
              tooltip="The country where this bank account is held"
            >
              Country of bank account
            </FieldLabel>
            {isLocked ? (
              <p className="txt-sm-regular text-text-secondary h-11 flex items-center px-4 border border-border-primary rounded-lg bg-surface-secondary">
                {countryData?.name ?? country}
              </p>
            ) : (
              <SingleCombobox
                options={allCountries}
                value={country}
                setValue={handleCountryChange}
                placeholder="Select country"
                error={errors.country}
                canClearSelection={false}
                disabled={disabled}
                renderTrigger={(val) => (
                  <span className="txt-sm-regular text-text-secondary">
                    {val.selectedOption?.label || val.placeholder}
                  </span>
                )}
              />
            )}
            {errors.country && isLocked && (
              <InputErrorMessage message={errors.country} />
            )}
          </FieldWrapper>

          <FieldWrapper>
            <FieldLabel htmlFor="bankName">Bank name</FieldLabel>
            <Input
              id="bankName"
              data-testid="bank-bankName"
              type="text"
              value={bankName}
              onChange={(e) => handleBaseChange('bankName', e.target.value)}
              onBlur={(e) => handleBaseBlur('bankName', e.target.value)}
              placeholder="e.g., HDFC Bank, Chase, Deutsche Bank"
              maxLength={120}
              error={errors.bankName}
              className="h-11"
              disabled={disabled}
            />
          </FieldWrapper>

          <FieldWrapper>
            <FieldLabel
              htmlFor="currency"
              tooltip={getFieldDescription(config, 'currency')}
            >
              Account currency
            </FieldLabel>
            <SingleCombobox
              options={ALL_CURRENCIES}
              value={currency}
              setValue={(val) => handleBaseChange('currency', val)}
              placeholder="Select currency"
              error={errors.currency}
              canClearSelection={false}
              disabled={disabled}
              renderTrigger={(val) => (
                <span className="txt-sm-regular text-text-secondary">
                  {val.selectedOption
                    ? val.selectedOption.value
                    : val.placeholder}
                </span>
              )}
            />
          </FieldWrapper>
        </div>
      </div>

      {/* Section 2 — Routing details (country-specific) */}
      {(requiredFields.length > 0 || optionalFields.length > 0) &&
        countryData && (
          <div className="flex flex-col gap-5">
            <SectionHeader
              title={`Bank details for ${countryData.name}`}
              subtitle="Country-specific identifiers required to send or receive payments."
            />
            <div className="flex flex-col gap-5">
              {requiredFields.map(renderLayer2Field)}
              {optionalFields.map(renderLayer2Field)}
            </div>
          </div>
        )}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function BankAccountFields({
  accountHolderName,
  country,
  bankName,
  currency,
  credentials,
  errors,
  onBaseFieldChange,
  onCredentialChange,
  onErrorChange,
  bankCountryScope,
  disabled = false,
}: BankAccountFieldsProps) {
  const { config, isLoading, error } = useBankAccountFieldsConfig();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingIndicator />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="flex items-center justify-center py-8 txt-small-regular text-text-secondary">
        Failed to load configuration. Please refresh and try again.
      </div>
    );
  }

  return (
    <BankAccountFieldsInner
      config={config}
      accountHolderName={accountHolderName}
      country={country}
      bankName={bankName}
      currency={currency}
      credentials={credentials}
      errors={errors}
      onBaseFieldChange={onBaseFieldChange}
      onCredentialChange={onCredentialChange}
      onErrorChange={onErrorChange}
      bankCountryScope={bankCountryScope}
      disabled={disabled}
    />
  );
}
